import _ from "lodash";

// Cómo se cuenta un control «Mantener en rango» en la lista, en el editor y en el panel del equipo. El usuario piensa
// en un rango (mínimo y máximo) y en si la salida hace subir o bajar la variable; el backend guarda los dos umbrales de
// la histéresis (on_threshold, off_threshold) y la dirección. Aquí se traduce entre las dos formas.
export const DIRECTION_OPTIONS = [
    { value: "raise", label: "Sube" },
    { value: "lower", label: "Baja" },
];

const NUMBER_FORMAT = new Intl.NumberFormat("es-CL", { maximumFractionDigits: 2 });

export const formatLoopValue = (value, unit) => {
    if (value === "" || _.isNil(value) || !Number.isFinite(Number(value))) return "–";
    return `${NUMBER_FORMAT.format(Number(value))}${unit ? ` ${unit}` : ""}`;
};

const isBlank = (value) => value === "" || _.isNil(value);

// Las mediciones con nombre masculino; el resto (temperatura, humedad, presión, luz…) va con «la».
const MASCULINE_FIELDS = ["co2"];

// «la temperatura», «el CO₂»: para armar frases. Una sigla (CO₂) no se pasa a minúscula.
export const variableWithArticle = (variable) => {
    if (!variable?.label) return "la medición";
    const { label, field = "" } = variable;
    const text = label.length > 1 && label[1] === label[1].toUpperCase() ? label : _.lowerFirst(label);
    const article = MASCULINE_FIELDS.includes(field) || field.startsWith("voltage") ? "el" : "la";
    return `${article} ${text}`;
};

// Encender hace subir la variable (raise): enciende en el mínimo y apaga en el máximo. Si la hace bajar, al revés.
export const rangeFromThresholds = ({ direction, on_threshold: on, off_threshold: off }) => (
    direction === "lower" ? { min: off, max: on } : { min: on, max: off }
);

export const thresholdsFromRange = ({ direction, min, max }) => (
    direction === "lower" ? { on_threshold: max, off_threshold: min } : { on_threshold: min, off_threshold: max }
);

export const rangeError = ({ min, max }) => {
    if (isBlank(min) || isBlank(max)) return null;
    if (!Number.isFinite(Number(min)) || !Number.isFinite(Number(max))) return "Escribe números.";
    if (Number(max) <= Number(min)) return "El máximo tiene que ser mayor que el mínimo.";
    return null;
};

// «Si la temperatura baja de 20 °C, se enciende «Calefactor»; al llegar a 20,3 °C, se apaga». `variable` ya trae su
// artículo (variableWithArticle).
export const describeLoopBehavior = ({ direction, min, max, unit, variable, output }) => {
    const subject = variable || "la medición";
    const name = output ? `«${output}»` : "la salida";
    return direction === "lower"
        ? `Si ${subject} sube de ${formatLoopValue(max, unit)}, se enciende ${name}; al bajar a ${formatLoopValue(min, unit)}, se apaga.`
        : `Si ${subject} baja de ${formatLoopValue(min, unit)}, se enciende ${name}; al llegar a ${formatLoopValue(max, unit)}, se apaga.`;
};

// «Entre 20 °C y 20,3 °C».
export const describeLoopRange = (loop) => {
    const { min, max } = rangeFromThresholds(loop || {});
    return `Entre ${formatLoopValue(min, loop?.measurement_unit)} y ${formatLoopValue(max, loop?.measurement_unit)}`;
};

// Frase completa para el panel y la lista: qué mantiene, en qué rango y qué pasa si el sensor calla.
export const describeControlLoop = (loop) => {
    if (!loop) return "";
    const variable = variableWithArticle({ field: loop.measurement, label: loop.measurement_label || loop.measurement });
    const sensor = loop.sensor?.name || loop.sensor_name;
    const parts = [`Mantiene ${variable}${sensor ? ` de «${sensor}»` : ""} ${_.lowerFirst(describeLoopRange(loop))}`];
    if (loop.lease_minutes) parts.push(`si el sensor no informa en ${loop.lease_minutes} min, vuelve a su estado seguro`);
    if (loop.max_on_hours) parts.push(`máximo ${NUMBER_FORMAT.format(Number(loop.max_on_hours))} h encendida`);
    return parts.join(" · ");
};

// «Tablero · Calefactor»: el equipo y el punto, para elegir la salida. Un punto sin renombrar ya trae el nombre del
// equipo («Tablero · Circuito 5»): no se repite.
export const actuatorLabel = (actuator) => {
    if (!actuator) return "";
    const name = actuator.name || "";
    if (!actuator.equipment_name || name.startsWith(actuator.equipment_name)) return name;
    return `${actuator.equipment_name} · ${name}`;
};
